import { Icon } from '../Icon';
import type { IconName } from '../Icon';

/**
 * B spec §2.4 — **the bottom tab bar**: four tabs, icons from the 28-icon kit, labels always
 * shown, 49 pt row + safe-area inset. Bottom-anchored because that is the comfortable
 * one-handed arc (skill §1) — the out-of-match shell is used with idle hands, but it is still
 * a phone held in one of them.
 *
 * Buttons, not links: navigation is view state (§2.2 — no URL router, no history). The active
 * tab is `aria-current="page"` — the accessible fact the accent color restates visually, so
 * the state is never hue-only (skill §3): active also gets the heavier label weight.
 *
 * Each tab's hit target is the full 49 pt row slice (≥44 pt secondary floor, `--tl-touch-
 * secondary`); the safe-area inset is *padding below the row*, so nothing tappable sits inside
 * the home-indicator zone (skill §1's gesture-conflict rule).
 */

export type TabId = 'matches' | 'opponents' | 'myTeam' | 'more';

const TABS: readonly { readonly id: TabId; readonly label: string; readonly icon: IconName }[] = [
  { id: 'matches', label: 'Matches', icon: 'pitch' },
  { id: 'opponents', label: 'Opponents', icon: 'opponent-file' },
  { id: 'myTeam', label: 'My Team', icon: 'roster' },
  { id: 'more', label: 'More', icon: 'archive' },
];

export interface TabBarBProps {
  readonly active: TabId;
  readonly onSelect: (tab: TabId) => void;
}

export function TabBarB({ active, onSelect }: TabBarBProps) {
  return (
    <nav className="tabbar-b" data-testid="tabbar-b" aria-label="Touchline">
      {TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className="tabbar-b__tab"
          data-testid={`tab-${tab.id}`}
          aria-current={tab.id === active ? 'page' : undefined}
          onClick={() => {
            onSelect(tab.id);
          }}
        >
          <Icon name={tab.icon} />
          <span className="tabbar-b__label">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}
